/**
 * Free matching bandana sync — auto-add/remove bandanas linked to qualifying pajama lines.
 * Config: window.SurgeBandanaConfig (shop metafields.cuddleclones.pj-bandana)
 */
(function () {
  'use strict';

  const FREE_PROP = '_free_bandana_gift';
  const LINK_PROP = '_unique_key';
  const BG_PROP = 'Background Color';

  const productCache = Object.create(null);

  function syncLog() {
    if (window.__surgeBandanaSyncDebug === false) return;
    console.log.apply(console, ['[surgeBandanaSync]'].concat(Array.prototype.slice.call(arguments)));
  }

  function cartHasFreeBandanaLines(cart) {
    return (cart && cart.items ? cart.items : []).some(isFreeBandanaLine);
  }

  function shouldRunSync(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { run: false, reason: 'empty cart' };
    }
    if (cartHasFreeBandanaLines(cart)) {
      return { run: true, reason: 'free bandana line present' };
    }
    return { run: true, reason: 'cart has items (check for qualifying pajamas)' };
  }

  function cartJsUrl(routeUrl, fallbackPath) {
    const base = routeUrl || fallbackPath;
    return String(base).endsWith('.js') ? base : base + '.js';
  }

  function getCartAddUrl() {
    return cartJsUrl(window.routes && window.routes.cart_add_url, '/cart/add');
  }

  function getCartChangeUrl() {
    return cartJsUrl(window.routes && window.routes.cart_change_url, '/cart/change');
  }

  function getConfig() {
    let cfg = window.SurgeBandanaConfig;
    if (!cfg) return null;
    if (Array.isArray(cfg)) cfg = cfg[0];
    return cfg || null;
  }

  function normalizeTag(tag) {
    return String(tag || '').toLowerCase().trim();
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) return null;
      const product = await res.json();
      productCache[handle] = product;
      return product;
    } catch (err) {
      console.warn('[surgeBandanaSync] product fetch failed:', handle, err);
      return null;
    }
  }

  function productHasTag(product, tag) {
    if (!product || !tag) return false;
    const needle = normalizeTag(tag);
    if (Array.isArray(product.tags)) {
      return product.tags.some(function (t) {
        return normalizeTag(t) === needle;
      });
    }
    if (typeof product.tags === 'string') {
      return product.tags.split(',').some(function (t) {
        return normalizeTag(t) === needle;
      });
    }
    return false;
  }

  /**
   * Full sets only: a pajama line earns a free bandana only when its SKU contains "SET"
   * (SET, SET4, YSET, LONGSET, SHORTSET). Tops-only (TOP) and bottoms-only (BTM) lines don't qualify.
   * Optional override: add "qualifying_sku_contains" to the pj_bandana JSON ("" turns the check off).
   */
  function skuQualifies(sku, config) {
    const needle = config.qualifying_sku_contains != null ? String(config.qualifying_sku_contains) : 'SET';
    if (!needle) return true;
    return String(sku || '').toUpperCase().indexOf(needle.toUpperCase()) !== -1;
  }

  async function isPajamaLine(item, config) {
    const product = await fetchProduct(item.handle);
    return productHasTag(product, config.qualifying_tag || 'pajama');
  }

  function sortedSkuColorMap(config) {
    const map = config.sku_color_map || [];
    return map.slice().sort(function (a, b) {
      return String(b && b.match ? b.match : '').length - String(a && a.match ? a.match : '').length;
    });
  }

  /**
   * Resolve pajama background color from variant SKU via sku_color_map.
   * Example SKU: PJ-LBD-L-SET-1-SLTGRYPAW -> match SLTGRYPAW -> "Slate Gray Paws"
   */
  function colorForSku(sku, config) {
    const skuStr = String(sku || '');
    const skuUpper = skuStr.toUpperCase();
    const map = sortedSkuColorMap(config);

    for (let i = 0; i < map.length; i++) {
      const entry = map[i];
      if (entry && entry.match && skuUpper.indexOf(String(entry.match).toUpperCase()) !== -1) {
        return entry.color;
      }
    }

    const parts = skuStr.split('-');
    const lastSeg = parts.length ? parts[parts.length - 1].toUpperCase() : '';
    if (lastSeg) {
      for (let j = 0; j < map.length; j++) {
        const row = map[j];
        if (row && String(row.match).toUpperCase() === lastSeg) {
          return row.color;
        }
      }
    }

    if (config.default_color) {
      console.warn('[surgeBandanaSync] SKU color not found, using default:', sku, '->', config.default_color);
      return config.default_color;
    }

    console.warn('[surgeBandanaSync] SKU color not found and no default_color:', sku);
    return null;
  }

  function variantColorName(entry) {
    return entry.color || entry.name || '';
  }

  function variantIdFromEntry(entry) {
    const raw = entry.variant_id != null ? entry.variant_id : entry.id;
    return raw != null ? Number(raw) : null;
  }

  function variantIdForColor(color, config) {
    if (!color) return null;
    const list = config.color_variants || [];
    const colorLower = String(color).toLowerCase();
    for (let i = 0; i < list.length; i++) {
      if (String(variantColorName(list[i])).toLowerCase() === colorLower) {
        return variantIdFromEntry(list[i]);
      }
    }
    return null;
  }

  /** Cart line key — stable per line; qty updates keep the same key. */
  function getPajamaLinkKey(item) {
    const props = item.properties || {};
    if (props._original_unique_key) {
      return String(props._original_unique_key).trim();
    }
    return String(item.key);
  }

  /** Map legacy bandana _unique_key to current pajama link key. */
  function normalizeBandanaLinkKey(linkKey, cart) {
    if (!linkKey) return '';
    const keyStr = String(linkKey).trim();

    // If the bandana's linkKey matches any current pajama's link key, we're good.
    const items = cart.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const props = item.properties || {};
      const orig = props._original_unique_key;
      if (orig != null && String(orig).trim() === keyStr) {
        return keyStr;
      }
      if (String(item.key) === keyStr) {
        return keyStr;
      }
    }

    return keyStr;
  }

  function cartFingerprint(cart) {
    if (!cart) return '0';
    const parts = (cart.items || []).map(function (item) {
      return String(item.key) + ':' + item.quantity;
    });
    return String(cart.item_count || 0) + '|' + parts.join(',');
  }

  function isFreeBandanaLine(item) {
    const props = item.properties || {};
    return String(props[FREE_PROP] || '').toLowerCase() === 'true';
  }

  function getBandanaLinkKey(item) {
    const props = item.properties || {};
    return props[LINK_PROP] != null ? String(props[LINK_PROP]).trim() : '';
  }

  function cartHasLineKey(cart, lineKey) {
    if (!cart || !lineKey) return false;
    return (cart.items || []).some(function (item) {
      return String(item.key) === String(lineKey);
    });
  }

  async function buildDesiredState(cart, config) {
    const desired = new Map();
    const items = cart.items || [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!(await isPajamaLine(item, config))) continue;
      if (!skuQualifies(item.sku, config)) continue;

      const linkKey = getPajamaLinkKey(item);
      const color = colorForSku(item.sku, config);
      const variantId = variantIdForColor(color, config);

      if (!variantId) {
        console.warn('[surgeBandanaSync] No bandana variant for color:', color, 'sku:', item.sku);
        continue;
      }

      if (desired.has(linkKey)) {
        const row = desired.get(linkKey);
        row.qty += item.quantity;
      } else {
        desired.set(linkKey, {
          linkKey: linkKey,
          variantId: variantId,
          qty: item.quantity,
          color: color,
        });
      }
    }

    return desired;
  }

  function buildExistingState(cart) {
    const existing = new Map();
    const orphans = [];
    const items = cart.items || [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!isFreeBandanaLine(item)) continue;

      const rawLinkKey = getBandanaLinkKey(item);
      if (!rawLinkKey) {
        orphans.push(item);
        continue;
      }

      const linkKey = normalizeBandanaLinkKey(rawLinkKey, cart);
      if (existing.has(linkKey)) {
        const row = existing.get(linkKey);
        row.qty += item.quantity;
        if (row.lineKeys) row.lineKeys.push(item.key);
      } else {
        existing.set(linkKey, {
          lineKey: item.key,
          lineKeys: [item.key],
          variantId: item.variant_id,
          qty: item.quantity,
        });
      }
    }

    return { existing: existing, orphans: orphans };
  }

  async function cartAdd(variantId, quantity, properties) {
    const res = await fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity, properties: properties }],
      }),
    });
    const body = await res.json();
    if (!res.ok) {
      throw new Error((body && (body.description || body.message)) || 'Failed to add bandana');
    }
    return body;
  }

  async function cartChange(lineKey, quantity, cart) {
    if (!lineKey) return null;

    if (cart && !cartHasLineKey(cart, lineKey)) {
      return null;
    }

    const res = await fetch(getCartChangeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(lineKey), quantity: quantity }),
    });
    const body = await res.json();
    if (!res.ok) {
      if (quantity === 0 && body && (body.status === 'bad_request' || body.status === 422)) {
        return null;
      }
      throw new Error((body && (body.description || body.message)) || 'Failed to update bandana line');
    }
    return body;
  }

  async function fetchCart() {
    const ts = new Date().getTime();
    const res = await fetch('/cart.js?t=' + ts, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    return await res.json();
  }

  function notifyCartUpdated(cart) {
    if (window.VanillaCartState && typeof window.VanillaCartState.updateCart === 'function') {
      window.VanillaCartState.updateCart(cart);
      return;
    }
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: cart, bubbles: true })
    );
  }

  async function waitForBandanaSyncIdle(maxMs) {
    const limit = maxMs || 3000;
    const start = Date.now();
    while (window.__surgeBandanaSyncing && Date.now() - start < limit) {
      await new Promise(function (resolve) {
        setTimeout(resolve, 25);
      });
    }
  }

  async function surgeBandanaSync(trigger, cartHint) {
    const config = getConfig();
    if (!config) {
      // syncLog('skipped — no SurgeBandanaConfig', trigger || '');
      return null;
    }

    // Fast-path: if the caller already has a cart snapshot and it is empty,
    // skip the /cart.js network call entirely — this is the main cause of
    // continuous cart.js polling when the cart is empty.
    if (cartHint) {
      const hintGate = shouldRunSync(cartHint);
      if (!hintGate.run) {
        // syncLog('no-op (hint)', hintGate.reason, { item_count: cartHint.item_count });
        window.__surgeBandanaLastFp = cartFingerprint(cartHint);
        return cartHint;
      }
    }

    if (window.__surgeBandanaSyncing) {
      // syncLog('already running — waiting', trigger || '');
      await waitForBandanaSyncIdle();
      return window.VanillaCartState && window.VanillaCartState.cart
        ? window.VanillaCartState.cart
        : fetchCart();
    }
    window.__surgeBandanaSyncing = true;
    // syncLog('started', trigger || 'manual');

    try {
      let cart = await fetchCart();
      const gate = shouldRunSync(cart);
      if (!gate.run) {
        // syncLog('no-op', gate.reason, { item_count: cart.item_count });
        window.__surgeBandanaLastFp = cartFingerprint(cart);
        return cart;
      }
      // syncLog('processing', gate.reason, { item_count: cart.item_count });

      let state = buildExistingState(cart);
      let mutated = false;

      for (let o = 0; o < state.orphans.length; o++) {
        const removed = await cartChange(state.orphans[o].key, 0, cart);
        if (removed !== null) {
          mutated = true;
          cart = removed;
        }
      }

      state = buildExistingState(cart);
      const desiredAfterOrphans = await buildDesiredState(cart, config);

      const removeKeys = [];
      state.existing.forEach(function (row, linkKey) {
        if (!desiredAfterOrphans.has(linkKey)) {
          if (row.lineKeys) {
            for (let i = 0; i < row.lineKeys.length; i++) {
              removeKeys.push(row.lineKeys[i]);
            }
          } else {
            removeKeys.push(row.lineKey);
          }
        } else if (row.lineKeys && row.lineKeys.length > 1) {
          for (let i = 1; i < row.lineKeys.length; i++) {
            removeKeys.push(row.lineKeys[i]);
          }
        }
      });

      for (let r = 0; r < removeKeys.length; r++) {
        const removed = await cartChange(removeKeys[r], 0, cart);
        if (removed !== null) {
          mutated = true;
          cart = removed;
        }
      }

      if (mutated) {
        cart = await fetchCart();
        mutated = false;
      }

      state = buildExistingState(cart);
      const desiredFinal = await buildDesiredState(cart, config);

      const upsertOps = [];
      desiredFinal.forEach(function (want, linkKey) {
        const have = state.existing.get(linkKey);
        const props = {};
        props[FREE_PROP] = 'true';
        props[LINK_PROP] = linkKey;
        props[BG_PROP] = want.color;

        if (!have) {
          upsertOps.push({ type: 'add', variantId: want.variantId, qty: want.qty, props: props });
          return;
        }

        const sameVariant = Number(have.variantId) === Number(want.variantId);
        const sameQty = Number(have.qty) === Number(want.qty);
        if (sameVariant && sameQty) return;

        if (sameVariant && !sameQty) {
          // syncLog('qty change detected', {
          //   linkKey: linkKey,
          //   from: have.qty,
          //   to: want.qty,
          //   lineKey: have.lineKey,
          // });
          upsertOps.push({
            type: 'updateQty',
            lineKey: have.lineKey,
            qty: want.qty,
          });
          return;
        }

        upsertOps.push({
          type: 'replace',
          lineKey: have.lineKey,
          variantId: want.variantId,
          qty: want.qty,
          props: props,
        });
      });

      for (let u = 0; u < upsertOps.length; u++) {
        const op = upsertOps[u];
        if (op.type === 'add') {
          const added = await cartAdd(op.variantId, op.qty, op.props);
          if (added !== null) {
            mutated = true;
            cart = await fetchCart();
          }
        } else if (op.type === 'updateQty') {
          const updated = await cartChange(op.lineKey, op.qty, cart);
          if (updated !== null) {
            mutated = true;
            cart = updated;
          }
        } else if (op.type === 'replace') {
          await cartChange(op.lineKey, 0, cart);
          const replaced = await cartAdd(op.variantId, op.qty, op.props);
          if (replaced !== null) {
            mutated = true;
            cart = await fetchCart();
          }
        }
      }

      if (mutated) {
        window.__surgeBandanaSuppressNextSync = true;
        notifyCartUpdated(cart);
        return cart;
      }

      // syncLog('finished — no changes needed', { item_count: cart.item_count });
      window.__surgeBandanaLastFp = cartFingerprint(cart);
      return cart;
    } catch (err) {
      console.error('[surgeBandanaSync]', err);
      return cart;
    } finally {
      window.__surgeBandanaSyncing = false;
    }
  }

  let syncDebounceTimer = null;

  document.addEventListener('cart:updated', function (e) {
    if (window.__surgeBandanaSyncing) {
      // syncLog('cart:updated ignored — sync in progress');
      return;
    }
    if (window.__surgeBandanaSuppressNextSync) {
      window.__surgeBandanaSuppressNextSync = false;
      // syncLog('cart:updated ignored — suppress flag (self-dispatched)');
      return;
    }
    if (window.__surgeBandanaSkipDebouncedSync) {
      // syncLog('cart:updated ignored — drawer qty will sync explicitly');
      return;
    }
    const cart = e.detail;
    const gate = shouldRunSync(cart);
    const fp = cartFingerprint(cart);
    // syncLog('cart:updated received', {
    //   item_count: cart && cart.item_count,
    //   run: gate.run,
    //   reason: gate.reason,
    //   fp: fp,
    // });
    if (!gate.run) {
      window.__surgeBandanaLastFp = fp;
      return;
    }
    if (fp === window.__surgeBandanaLastFp) {
      // syncLog('cart:updated skipped — same fingerprint as last sync');
      return;
    }
    clearTimeout(syncDebounceTimer);
    // Pass the event cart as a hint so the sync can bail before fetching /cart.js
    // when the cart is already known to be empty (avoids continuous polling).
    var hintCart = cart;
    syncDebounceTimer = setTimeout(function () {
      surgeBandanaSync('cart:updated', hintCart).then(function (result) {
        if (result) window.__surgeBandanaLastFp = cartFingerprint(result);
      });
    }, 120);
  });

  window.surgeBandanaSync = surgeBandanaSync;
  window.surgeAwaitBandanaSyncIdle = waitForBandanaSyncIdle;
})();
